<script setup lang="ts">
import {onMounted, onUnmounted, ref} from "vue";
import "@milkdown/crepe/theme/common/style.css";
import {Crepe} from "@milkdown/crepe";
import {Milkdown, useEditor} from "@milkdown/vue";

const model = defineModel<string>();
const editor = ref<Crepe | null>(null);
useEditor((root) => {
    return (editor.value = new Crepe({
        root,
        defaultValue: model.value,
    }));
});

defineExpose({
    editor,
});
</script>
<template>
    <Milkdown />
</template>
<style>
/* Light Theme */
.milkdown {
    /* Background Colors - Match PrimeVue inputs */
    --crepe-color-background: #fafaf9; /* stone-50 - same as input background */
    --crepe-color-surface: #fafaf9; /* stone-50 - match container */
    --crepe-color-surface-low: #f5f5f4; /* stone-100 */

    /* Text Colors */
    --crepe-color-on-background: #57534e; /* stone-600 */
    --crepe-color-on-surface: #44403c; /* stone-700 */
    --crepe-color-on-surface-variant: #78716c; /* stone-500 */

    /* Accent Colors */
    --crepe-color-primary: #44ab89; /* askr-green */
    --crepe-color-secondary: #d6d3d1; /* stone-300 */
    --crepe-color-on-secondary: #1c1917; /* stone-900 */

    /* UI Colors */
    --crepe-color-outline: #a8a29e; /* stone-400 */
    --crepe-color-error: #dc2626; /* red-600 */
    --crepe-color-inline-code: #ea580c; /* orange-600 */

    /* Interactive Colors */
    --crepe-color-hover: #f5f5f4; /* stone-100 */
    --crepe-color-selected: #e7e5e4; /* stone-200 */
    --crepe-color-inline-area: #e7e5e4; /* stone-200 */
    --crepe-color-inverse: #292524; /* stone-800 */
    --crepe-color-on-inverse: #fafaf9; /* stone-50 */

    /* Typography */
    --crepe-font-title: Poppins, Georgia, serif;
    --crepe-font-default: Poppins, Arial, sans-serif;
    --crepe-font-code: "Courier New", Courier, monospace;

    /* Shadows */
    --crepe-shadow-1: 0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3);
    --crepe-shadow-2: 0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3);
}

/* Dark Theme */
.dark .milkdown {
    /* Background Colors - Match PrimeVue inputs */
    --crepe-color-background: #1c1917; /* stone-900 - same as input background */
    --crepe-color-surface: #1c1917; /* stone-900 - match container */
    --crepe-color-surface-low: #292524; /* stone-800 */

    /* Text Colors - Match PrimeVue inputs */
    --crepe-color-on-background: #e7e5e4; /* stone-200 - same as input text */
    --crepe-color-on-surface: #e7e5e4; /* stone-200 - match inputs */
    --crepe-color-on-surface-variant: #a8a29e; /* stone-400 */

    /* Accent Colors */
    --crepe-color-primary: #44ab89; /* askr-green */
    --crepe-color-secondary: #44403c; /* stone-700 */
    --crepe-color-on-secondary: #e7e5e4; /* stone-200 */

    /* UI Colors */
    --crepe-color-outline: #57534e; /* stone-600 */
    --crepe-color-error: #ef4444; /* red-500 - brighter for dark */
    --crepe-color-inline-code: #fb923c; /* orange-400 - brighter for dark */

    /* Interactive Colors */
    --crepe-color-hover: #292524; /* stone-800 */
    --crepe-color-selected: #44403c; /* stone-700 */
    --crepe-color-inline-area: #44403c; /* stone-700 */
    --crepe-color-inverse: #e7e5e4; /* stone-200 */
    --crepe-color-on-inverse: #1c1917; /* stone-900 */

    /* Typography - same as light theme */
    --crepe-font-title: Poppins, Georgia, serif;
    --crepe-font-default: Poppins, Arial, sans-serif;
    --crepe-font-code: "Courier New", Courier, monospace;

    /* Shadows - lighter for dark mode */
    --crepe-shadow-1: 0px 1px 2px 0px rgba(255, 255, 255, 0.3), 0px 1px 3px 1px rgba(255, 255, 255, 0.15);
    --crepe-shadow-2: 0px 1px 2px 0px rgba(255, 255, 255, 0.3), 0px 2px 6px 2px rgba(255, 255, 255, 0.15);
}

/* Custom Editor Styling - Match PrimeVue Inputs */
.milkdown {
    /* Container styling to match PrimeVue inputs */
    border-radius: 0.375rem; /* rounded-md */
    border: 1px solid #d6d3d1; /* border-stone-300 */
    background-color: #fafaf9; /* bg-stone-50 */
    transition:
        border-color 200ms,
        box-shadow 200ms;
}

.milkdown:hover {
    border-color: #44ab89; /* hover:border-askr-green */
}

.milkdown:focus-within {
    outline: none;
    outline-offset: 0;
    border-color: #44ab89;
    box-shadow: 0 0 0 3px rgba(68, 171, 137, 0.5); /* ring-askr-green-500/50 */
}

/* Dark mode */
.dark .milkdown {
    background-color: #1c1917; /* dark:bg-stone-900 */
    border-color: #57534e; /* dark:border-stone-600 */
}

.dark .milkdown:focus-within {
    box-shadow: 0 0 0 3px rgba(68, 171, 137, 0.4); /* dark:ring-askr-green-400/50 */
}

/* Editor content area */
/*
.milkdown .editor {
    max-width: 100%;
    padding: 0.75rem;
    min-height: 200px;
}

.milkdown .editor p {
    margin: 0.75rem 0;
    line-height: 1.6;
}*/

.milkdown .editor h1,
.milkdown .editor h2,
.milkdown .editor h3 {
    font-weight: 600;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
}

.milkdown .ProseMirror h1 {
    font-size: 2rem;
}

.milkdown .ProseMirror h2 {
    font-size: 1.5rem;
}

.milkdown .ProseMirror h3 {
    font-size: 1.25rem;
}
</style>
